/* <card-gallery> — mobile-only swipeable image gallery for product cards.

   The scrolling itself is native CSS scroll-snap (see component-card-gallery.css);
   this element only:
     - syncs the active dot to the visible slide (one IntersectionObserver),
     - keeps the card parked on the first image until the shopper swipes it,
     - lets a dot tap scroll to that slide,
     - guards the card's <a> link so a horizontal swipe never opens the PDP.

   It does nothing on desktop / non-touch devices (matchMedia gate), matching the
   CSS which hides the gallery there. */
if (!customElements.get("card-gallery")) {
  customElements.define(
    "card-gallery",
    class CardGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector(".card__gallery-track");
        this.slides = Array.from(this.querySelectorAll(".card__gallery-slide"));
        this.dots = Array.from(this.querySelectorAll(".card__gallery-dot"));
        this.activeIndex = 0;

        // Has the shopper deliberately moved THIS gallery? Only a horizontal
        // gesture counts — see _handlePointerMove / _handleTrackScroll.
        this._userInteracted = false;
        // True only while _pinToStart() is writing scrollLeft, so the scroll
        // event it provokes is not mistaken for a swipe.
        this._pinning = false;
        this._pointerActive = false;
        this._axis = null;

        // Nothing to do for a single image (gallery shouldn't render, but guard anyway).
        if (!this.track || this.slides.length < 2) return;

        // Only operate on the mobile grid breakpoint; CSS hides the gallery elsewhere.
        this.mq = window.matchMedia("(max-width: 899px)");
        this._onMqChange = () => this._sync();
        if (this.mq.addEventListener) {
          this.mq.addEventListener("change", this._onMqChange);
        } else if (this.mq.addListener) {
          this.mq.addListener(this._onMqChange); // Safari < 14
        }

        this._onClick = this._handleClick.bind(this);
        this._onPointerDown = this._handlePointerDown.bind(this);
        this._onPointerMove = this._handlePointerMove.bind(this);
        this._onPointerEnd = this._handlePointerEnd.bind(this);
        this._onTrackScroll = this._handleTrackScroll.bind(this);
        this.addEventListener("click", this._onClick);
        this.addEventListener("pointerdown", this._onPointerDown);
        this.addEventListener("pointermove", this._onPointerMove);
        this.addEventListener("pointerup", this._onPointerEnd);
        this.addEventListener("pointercancel", this._onPointerEnd);
        this.track.addEventListener("scroll", this._onTrackScroll, {
          passive: true,
        });

        this._sync();
        this._setupPinTriggers();
      }

      disconnectedCallback() {
        this._teardownObserver();
        this._teardownVisibilityObserver();
        if (this.mq) {
          if (this.mq.removeEventListener) {
            this.mq.removeEventListener("change", this._onMqChange);
          } else if (this.mq.removeListener) {
            this.mq.removeListener(this._onMqChange);
          }
        }
        this.removeEventListener("click", this._onClick);
        this.removeEventListener("pointerdown", this._onPointerDown);
        this.removeEventListener("pointermove", this._onPointerMove);
        this.removeEventListener("pointerup", this._onPointerEnd);
        this.removeEventListener("pointercancel", this._onPointerEnd);
        if (this.track && this._onTrackScroll) {
          this.track.removeEventListener("scroll", this._onTrackScroll);
        }
        if (this._rafPin) cancelAnimationFrame(this._rafPin);
        if (this._onWindowLoad) {
          window.removeEventListener("load", this._onWindowLoad);
        }
        if (this._onPageShow) {
          window.removeEventListener("pageshow", this._onPageShow);
        }
        (this._imgLoadCleanups || []).forEach((off) => off());
        this._imgLoadCleanups = [];
      }

      /* Everything that can leave a card parked on the wrong slide, and the
         re-pin that answers it. The window used to close at window.load, which
         is well before the real trouble starts: a card below the fold has not
         decoded its lazy slide images yet, and iOS Safari re-runs `scroll-snap-
         type: x mandatory` when they land — which is exactly "while scrolling",
         as reported. bfcache restore is the other one: coming back via the
         Safari back button replays neither connectedCallback nor load. */
      _setupPinTriggers() {
        this._pinToStart();
        this._rafPin = requestAnimationFrame(() => this._pinToStart());

        this._onWindowLoad = () => this._pinToStart();
        window.addEventListener("load", this._onWindowLoad, { once: true });

        // Re-pin as each slide image decodes, not just the first — the later
        // ones are lazy and land long after load.
        this._imgLoadCleanups = [];
        this.slides.forEach((slide) => {
          const img = slide.querySelector("img");
          if (!img || img.complete) return;
          const onLoad = () => this._pinToStart();
          img.addEventListener("load", onLoad, { once: true });
          this._imgLoadCleanups.push(() =>
            img.removeEventListener("load", onLoad)
          );
        });

        // Re-pin when the card is actually scrolled into view, which is when
        // its lazy images start loading and the snap engine re-runs.
        this._setupVisibilityObserver();

        // bfcache / back-forward restore.
        this._onPageShow = () => this._pinToStart();
        window.addEventListener("pageshow", this._onPageShow);
      }

      _setupVisibilityObserver() {
        if (this.visibilityObserver || !("IntersectionObserver" in window)) return;
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this._pinToStart();
              // One more after the images have had a moment to settle.
              window.setTimeout(() => this._pinToStart(), 400);
            });
          },
          { rootMargin: "200px 0px" }
        );
        this.visibilityObserver.observe(this);
      }

      _teardownVisibilityObserver() {
        if (this.visibilityObserver) {
          this.visibilityObserver.disconnect();
          this.visibilityObserver = null;
        }
      }

      _sync() {
        if (this.mq && this.mq.matches) {
          this._setupObserver();
        } else {
          this._teardownObserver();
        }
      }

      _setupObserver() {
        if (this.observer) return;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                const idx = this.slides.indexOf(entry.target);
                if (idx !== -1) this._setActive(idx);
              }
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
      }

      _teardownObserver() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
      }

      _setActive(idx) {
        if (idx === this.activeIndex) return;
        this._forceActive(idx);
      }

      _forceActive(idx) {
        this.activeIndex = idx;
        this.dots.forEach((dot, i) =>
          dot.classList.toggle("is-active", i === idx)
        );
      }

      // Snap the track back to the first slide unless the shopper has taken over.
      // No-op for already-correct cards (so we never flicker the common case)
      // and on desktop, where the gallery is hidden.
      _pinToStart() {
        if (this._userInteracted) return;
        if (this.mq && !this.mq.matches) return;
        if (!this.track) return;
        if (this.track.scrollLeft === 0 && this.activeIndex === 0) return;

        // Briefly disable mandatory snap so the snap engine doesn't fight the
        // reset; CSS re-applies it next frame, snapping cleanly to slide 0.
        const prevSnap = this.track.style.scrollSnapType;
        this._pinning = true;
        this.track.style.scrollSnapType = "none";
        this.track.scrollLeft = 0;
        this._forceActive(0);
        requestAnimationFrame(() => {
          if (this.track) this.track.style.scrollSnapType = prevSnap;
          // Scroll events are delivered async; stay guarded until after them.
          requestAnimationFrame(() => {
            this._pinning = false;
          });
        });
      }

      _handlePointerDown(event) {
        this._pointerActive = true;
        this._axis = null;
        this._startX = event.clientX;
        this._startY = event.clientY;
        this._startPageY = window.scrollY;
        this._startScroll = this.track ? this.track.scrollLeft : 0;
      }

      /* Decide the gesture's axis. A vertical drag that merely STARTED on this
         card is the shopper scrolling the page past it — it must not count as
         taking over the gallery, or the card loses its self-correction for the
         rest of the page's life. That blanket "any pointerdown = interacted"
         is what made the reported bug stick. */
      _handlePointerMove(event) {
        if (!this._pointerActive || this._axis) return;
        const dx = Math.abs(event.clientX - this._startX);
        const dy = Math.abs(event.clientY - this._startY);
        if (dx < 5 && dy < 5) return; // below the noise floor — undecided
        this._axis = dx > dy ? "x" : "y";
        if (this._axis === "x") this._userInteracted = true;
      }

      _handlePointerEnd() {
        this._pointerActive = false;
        this._lastPointerEnd = Date.now();
      }

      /* The reliable takeover signal: the track really moved. Count it only
         when a finger is (or just was) on the card and the gesture is not a
         vertical page scroll. A scroll with no touch behind it is the browser
         re-snapping, and that is precisely what we want to undo. */
      _handleTrackScroll() {
        if (this._pinning || this._userInteracted) return;
        if (this._axis === "y") return;
        // iOS can cancel the pointer before a single move resolves the axis, so
        // corroborate with the page itself: if it scrolled under this gesture,
        // the finger was travelling vertically and the gallery was not swiped.
        if (Math.abs(window.scrollY - (this._startPageY || 0)) > 4) return;
        const settling = Date.now() - (this._lastPointerEnd || 0) < 1200;
        if (this._pointerActive || settling) this._userInteracted = true;
      }

      _handleClick(event) {
        // Dot tap → scroll to that slide; never navigate.
        const dot = event.target.closest(".card__gallery-dot");
        if (dot) {
          event.preventDefault();
          event.stopPropagation();
          const idx = Number(dot.dataset.goto) || 0;
          if (this.slides[idx] && this.track) {
            this._userInteracted = true; // a tap is deliberate
            const motionOK = !window.matchMedia(
              "(prefers-reduced-motion: reduce)"
            ).matches;
            // scrollTo on the track, not scrollIntoView: the latter also
            // scrolls every ancestor, which jumps the page on mobile.
            this.track.scrollTo({
              left: idx * this.track.clientWidth,
              behavior: motionOK ? "smooth" : "auto",
            });
          }
          return;
        }

        // Click-guard: if the pointer dragged horizontally (or the track scrolled),
        // this "click" is the tail of a swipe — cancel the card link navigation.
        const movedScroll = this.track
          ? Math.abs(this.track.scrollLeft - (this._startScroll || 0))
          : 0;
        const movedPointer =
          this._startX != null ? Math.abs(event.clientX - this._startX) : 0;
        if (movedScroll > 8 || movedPointer > 10) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    }
  );
}
